import React, { useState } from 'react';
import { getTours, TourEdit, Tour, getTourSalesStats } from '../../../model/tour';
import { useData, usePaginatedResponse } from '../../../model/data';
import { Icon } from '../../../components/Icon/Icon';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { Pagination } from '../../../components/Pagination/Pagination';
import { SortableList } from '../../../components/SortableList/SortableList';
import { parseDate, formatDateTime } from '../../../util/util';
import { SalesDonut } from '../../../components/SalesDonut/SalesDonut';
import { serializeQuery } from '../../../model/filter';
import { tourPath } from '../../../routes';
import { useMeta } from '../../../model/meta';
import { TourForm } from './TourCard/TourForm';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import { useQueryAsState } from '../../../model/history';
import { FilterBar } from '../../../components/FilterBar/FilterBar';
import { isAdmin, useSelectedMembership } from '../../../model/membership';
import { t } from '../../../locales/translate';
import { Effect } from '../../../components/Effect/Effect';
import { ViewportObserver } from '../../../components/ViewportObserver/ViewportObserver';
import { compact } from 'lodash';
import { SortByIcon } from '../../../components/SortableList/SortByIcon';

export function Tours(): JSX.Element {
  const [showAddModal, setShowAddModal] = useState(false);
  const [params] = useQueryAsState();

  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getTours({
        term: serializeQuery(params.query),
        page,
        perPage,
        timeframe: params.timeframe,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      }),
    JSON.stringify(params)
  );

  useMeta({
    title: t.model.tour._plural,
  });

  const tours = useData((db) => compact(paginator.currentIds.map((e) => db.table('tours').find(e))));

  const membership = useSelectedMembership();

  const addTourButton = isAdmin(membership) && (
    <Icon
      name="plus"
      onClick={() => {
        setShowAddModal(true);
      }}
    />
  );

  function formatTourName(tour: Tour): string | undefined {
    if (tour.internalName) {
      return `${tour.internalName} (${tour.name})`;
    } else {
      return tour.name;
    }
  }

  return (
    <Container className="tours">
      <Card
        title={t.model.tour._plural}
        elevation={2}
        actions={addTourButton}
        pagination={<Pagination {...paginator} />}
        fullbleed
      >
        <SortableList
          items={tours}
          error={paginator.error}
          actions={<FilterBar filters={['query', 'timeframe']} />}
          rowLink={(row) => tourPath(row)}
          loading={paginator.loading}
          emptyMessage={t.model.tour.noTours}
          columns={[
            {
              width: 120,
              label: t.model.datetime.startDate,
              apiSortIcon: <SortByIcon sortBy="start_date" />,
              dataKey: 'startDate',
              cellRenderer: (row) => (
                <div>
                  {formatDateTime(parseDate(row.startDate || ''))}
                  {' - '}
                  {row.endDate && formatDateTime(parseDate(row.endDate || ''))}
                </div>
              ),
            },
            {
              width: 300,
              label: t.model.tour.tableInternalTitle,
              sortable: false,
              dataKey: 'internalName',
              cellRenderer: (row) => formatTourName(row),
            },
            {
              width: 150,
              label: t.model.datetime.createdAt,
              sortable: false,
              dataKey: 'createdAt',
              cellRenderer: (row) => formatDateTime(parseDate(row.createdAt || '')),
            },
            {
              width: 80,
              label: t.model.sale.salesStats,
              sortable: false,
              dataKey: 'salesStats',
              cellRenderer: (row) => (
                <ViewportObserver
                  onEnter={() => {
                    !row.salesStats && getTourSalesStats(row.id);
                  }}
                >
                  <Effect>{row.salesStats && <SalesDonut data={row.salesStats} size="table" />}</Effect>
                </ViewportObserver>
              ),
            },
          ]}
        />
      </Card>
      <ControlledModal<TourEdit>
        open={showAddModal}
        model={{} as TourEdit}
        onClose={() => setShowAddModal(false)}
        title={t.actions.add(t.model.tour._name)}
        size="small"
      >
        {(controller) => <TourForm controller={controller} />}
      </ControlledModal>
    </Container>
  );
}
